import clsx from 'clsx';
import type * as React from 'react';

import { getStorageCachedURL, isImageFile, isOpenAPIFile } from '@gitbook/app-database';
import type * as hiveCore from '@gitbook/hive-core';
import { Icon, type SVGIconProps } from '@gitbook/spine-icons';
import { Pressable } from '@gitbook/spine-react';

import { getImageResizedURL } from '../../utils';
import { useMediaGallery } from '../MediaGallery';

/**
 * Render an icon for a file.
 */
export function FileIcon(props: {
    file?: hiveCore.RevisionFile;
    size?: number;
    className?: string;
    zoomable?: boolean;
}): React.ReactElement {
    const { file, size = 18, className, zoomable = false } = props;

    const mediaGallery = useMediaGallery();

    const isImage = !!file && isImageFile(file);
    const imageZoomable = isImage && zoomable;

    const icon = (
        <div
            className={clsx(
                'flex items-center justify-center overflow-hidden rounded bg-muted text-muted',
                imageZoomable && ['cursor-zoom-in', 'focus-visible:shadow-focus-ring'],
                className
            )}
            style={{ width: size, height: size }}
            tabIndex={imageZoomable ? 0 : undefined}
        >
            {file && isImage ? (
                <img
                    alt={file.name}
                    src={getImageResizedURL(file.downloadURL, {
                        width: size,
                    })}
                    className="size-full object-contain"
                />
            ) : (
                <SVGIconForFile file={file} size={size * 0.65} />
            )}
        </div>
    );

    if (!imageZoomable) {
        return icon;
    }

    return (
        <Pressable
            onPress={() => {
                mediaGallery.open([
                    {
                        url: getStorageCachedURL(file.downloadURL),
                    },
                ]);
            }}
        >
            {icon}
        </Pressable>
    );
}

function SVGIconForFile(
    props: {
        file?: File | hiveCore.RevisionFile;
    } & SVGIconProps
) {
    const { file, ...rest } = props;

    if (!file) {
        return <Icon.Trashcan {...rest} />;
    }

    if (isOpenAPIFile(file)) {
        return <Icon.OpenAPI {...rest} />;
    }

    if (file.name.endsWith('.pdf')) {
        return <Icon.PDF {...rest} />;
    }

    return <Icon.Paperclip {...rest} />;
}
